import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'
import CloseButton from '@/components/close-button'
import { WarningLarge } from '@/components/icons'
import { createModal, useModalize } from '@/components/modalize'
import { toast } from '@/components/toast-notifications'
import { breakpoints, colors, fontWeights } from '@/styles'
import useMediaQuery from '@/hooks/use-media-query'

const Wrapper = styled.div`
  background: ${colors.white};
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-shrink: 0;
  height: 100%;
  max-height: 98vh;
  padding: 40px;

  @media screen and (max-width: ${breakpoints.ipadMiniMax}) {
    padding: 32px;
  }
`

const Title = styled.h3`
  display: flex;
  align-items: center;
  font-size: 24px;
  font-weight: ${fontWeights.bold};
  line-height: 32px;
  margin: 24px 0 12px;
  width: 100%;
  text-align: center;
  justify-content: center;
`

const Text = styled.p`
  margin-bottom: 24px;
  color: ${colors.grey48};
  font-size: 16px;
  line-height: 24px;
  text-align: center;
`
const ButtonContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 8px;
  width: 100%;
  max-width: 370px;
  height: 100%;
  margin: 0 auto;
`

const Button = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 20px;
  width: 100%;
  height: 48px;
  max-width: 176px;
  background-color: ${colors.white};
  color: ${colors.blue16};
  border: 1.5px solid ${colors.grey88};
  border-radius: 6px;
  flex-grow: 1;
  cursor: pointer;

  :hover {
    opacity: 0.6;
  }
`

const HideButton = styled(Button)`
  background-color: ${colors.blue16};
  color: ${colors.white};
  box-shadow: 0px 16px 32px -8px rgba(0, 0, 0, 0.15);
`

const HidePhoneModal = ({ hidden, setIsPhoneHidden }) => {
  const [hiding, setHiding] = useState(false)
  const { close } = useModalize()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const showToast = () =>
    toast(t('l.chat.view.hide_phone_text_success.text', 'Phone number visibility updated!'), {
      title: t('l.chat.view.hide_phone_text_success.title', 'Update Successful'),
      type: 'success'
    })

  const handleHide = async () => {
    if (hiding) return
    setHiding(true)
    const { response } = await dispatch.chat.hidePhoneNumber()
    if (response.ok) {
      setHiding(false)
      setIsPhoneHidden(true)
      showToast()
      close()
    }
  }

  const handleUnhide = async () => {
    if (hiding) return
    setHiding(true)
    const { response } = await dispatch.chat.unhidePhoneNumber()
    if (response.ok) {
      setHiding(false)
      setIsPhoneHidden(false)
      showToast()
      close()
    }
  }
  const isMobile = useMediaQuery(`(max-width: ${breakpoints.ipadMiniMax})`)

  return (
    <Wrapper>
      {isMobile && <CloseButton width={14} height={14} top={29} right={21} onClick={close} />}
      <WarningLarge />
      <Title>
        {hidden
          ? t(
              'l.chat.view.unhide_phone_modal.title',
              'Are you sure you want to unhide your phone number?'
            )
          : t(
              'l.chat.view.hide_phone_modal.title',
              'Are you sure you want to hide your phone number?'
            )}
      </Title>
      <Text>
        {hidden
          ? t(
              'l.chat.view.unide_phone_modal.text',
              'Your phone number will be displayed to the public in your listings and chat messages.'
            )
          : t(
              'l.chat.view.hide_phone_modal.text',
              'Your phone number will not be displayed to the public in your listings and chat messages.'
            )}
      </Text>
      <ButtonContainer>
        <Button onClick={close}>{t('common.cancel', 'Cancel')}</Button>
        <HideButton onClick={hidden ? handleUnhide : handleHide}>
          {hidden ? t('l.chat.view.unhide.button', 'Unhide') : t('l.chat.view.hide.button', 'Hide')}
        </HideButton>
      </ButtonContainer>
    </Wrapper>
  )
}

HidePhoneModal.displayName = 'HidePhoneModal'

export const showHidePhoneModal = createModal(HidePhoneModal, {
  width: 'auto',
  maxWidth: '440px',
  flexGrow: true,
  border: '1px solid #d8d8d8',
  tabletFullscreen: true
})
